import { FC } from 'react';
import { Box, Chip, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DocumentClassificationInvalidReason } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { AssessmentDomainDocumentDetail } from '@libs/types';
import { formatFileSize } from '@libs/utils';
import {
  CheckCircleIcon,
  DownloadSimpleIcon,
  EyeIcon,
  LinkBreakIcon,
  QuestionIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import { Badge } from '../../../../components/Badge';
import { TooltipCard } from '../../../../components/Tooltip';
import { AssessmentDocumentChangeBadge } from '../workflow/AssessmentDocumentChangeBadge';

interface DocumentItemProps {
  /**
   * The document. Widened to `AssessmentDomainDocumentDetail` so callers can
   * pass the parent-version `changeType` annotation through (Unchanged/Added)
   * without needing to project it onto a sub-shape.
   */
  document: AssessmentDomainDocumentDetail;
  domainName: string;
  /** Parent assessment version, e.g. 3 → "FROM V3". `undefined` for v1. */
  parentVersion?: number;
  onDownload: (documentId: string) => void;
  onView?: (documentId: string) => void;
}

type ClassificationState =
  | { kind: 'valid'; name: string; key: string }
  | { kind: 'not_classified' }
  | { kind: 'no_match' }
  /** Persisted invalidReason WRONG_DOMAIN (low confidence with key + name). */
  | { kind: 'wrong_domain'; name: string; key: string }
  /**
   * Persisted invalidReason WRONG_FORMAT — the right document in an accepted
   * but non-standard format. Advisory: the match still counts.
   */
  | { kind: 'wrong_format'; name: string; key: string }
  /**
   * Persisted invalidReason UNSUPPORTED_FORMAT — the right document in a format
   * that is neither standard nor accepted, so it cannot be validated. Blocking.
   */
  | { kind: 'unsupported_format'; name: string; key: string };

function getClassificationState(document: AssessmentDomainDocumentDetail): ClassificationState {
  const meta = document.classificationMetadata;

  if (!meta) return { kind: 'not_classified' };

  const resolved = meta.invalidReason;

  if (resolved === DocumentClassificationInvalidReason.NO_MATCH) {
    return { kind: 'no_match' };
  }

  if (resolved === DocumentClassificationInvalidReason.WRONG_DOMAIN) {
    const name = meta.matchedStandardDocumentName ?? meta.matchedStandardDocumentKey ?? '';
    const key = meta.matchedStandardDocumentKey ?? '';
    return { kind: 'wrong_domain', name, key };
  }

  if (
    resolved === DocumentClassificationInvalidReason.WRONG_FORMAT ||
    resolved === DocumentClassificationInvalidReason.UNSUPPORTED_FORMAT
  ) {
    const name = meta.matchedStandardDocumentName ?? meta.matchedStandardDocumentKey ?? '';
    const key = meta.matchedStandardDocumentKey ?? '';
    const kind =
      resolved === DocumentClassificationInvalidReason.WRONG_FORMAT
        ? 'wrong_format'
        : 'unsupported_format';
    return { kind, name, key };
  }

  const name = meta.matchedStandardDocumentName ?? meta.matchedStandardDocumentKey ?? '';
  const key = meta.matchedStandardDocumentKey ?? '';
  return { kind: 'valid', name, key };
}

export const DocumentItem: FC<DocumentItemProps> = observer(
  ({ document, domainName, parentVersion, onDownload, onView }) => {
    const { t } = useTrans();
    const theme = useTheme();

    const state = getClassificationState(document);
    const isInvalid = state.kind !== 'valid';

    const formatUploadDate = (dateString: string): string => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDownload(document.documentId);
    };

    const handleView = (e: React.MouseEvent) => {
      e.stopPropagation();
      onView?.(document.documentId);
    };

    // Renders the document name + key pill used in classified states
    const DocRef = observer(({ name, docKey }: { name: string; docKey: string }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <TooltipCard variant="dark" description={name} placement="top">
          <Typography
            variant="textXS_semiBold"
            sx={{
              color: isInvalid ? 'warning.dark' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
        </TooltipCard>
        <Box
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: isInvalid ? 'warning.light' : 'grey.100',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="textXS_regular"
            sx={{
              fontFamily: 'monospace',
              color: isInvalid ? 'warning.dark' : 'text.secondary',
            }}
          >
            {docKey}
          </Typography>
        </Box>
      </Box>
    ));

    const renderClassificationRow = () => {
      switch (state.kind) {
        case 'valid':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'green.50',
                borderTop: `1px solid ${theme.palette.green[200]}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <CheckCircleIcon size={15} weight="fill" color={theme.palette.green[600]} />
              <DocRef name={state.name} docKey={state.key} />
            </Box>
          );

        case 'not_classified':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'grey.50',
                borderTop: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QuestionIcon size={15} weight="fill" color={theme.palette.text.disabled} />
                <Typography variant="textXS_regular" sx={{ color: 'text.disabled' }}>
                  <Trans i18nKey="app.assessment.classification.warning.reason.unclassified.label" />
                </Typography>
              </Box>
              <Chip
                label={<Trans i18nKey="assessments.summary.classification.wontBeSubmitted" />}
                size="small"
                sx={{
                  backgroundColor: 'grey.200',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  flexShrink: 0,
                }}
              />
            </Box>
          );

        case 'no_match':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'warning.lighter',
                borderTop: `1px solid ${theme.palette.warning.light}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkBreakIcon size={15} color={theme.palette.warning.dark} />
                <Typography variant="textXS_regular" sx={{ color: 'warning.dark' }}>
                  <Trans i18nKey="assessments.summary.classification.noMatch" />
                </Typography>
              </Box>
              <Chip
                label={<Trans i18nKey="assessments.summary.classification.wontBeSubmitted" />}
                size="small"
                sx={{
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  flexShrink: 0,
                }}
              />
            </Box>
          );

        case 'wrong_domain':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'warning.lighter',
                borderTop: `1px solid ${theme.palette.warning.light}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <WarningCircleIcon size={15} weight="fill" color={theme.palette.warning.dark} style={{ flexShrink: 0 }} />
                <Typography variant="textXS_regular" sx={{ color: 'warning.dark', flexShrink: 0 }}>
                  <Trans i18nKey="assessments.summary.classification.invalidDocument" />
                </Typography>
              </Box>
              <Chip
                label={<Trans i18nKey="assessments.summary.classification.wontBeSubmitted" />}
                size="small"
                sx={{
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  flexShrink: 0,
                }}
              />
            </Box>
          );

        // The document matched correctly and still counts, so this row shows
        // the matched reference and omits the "won't be submitted" chip the
        // blocking reasons carry — only the file's format is flagged.
        case 'wrong_format':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'warning.lighter',
                borderTop: `1px solid ${theme.palette.warning.light}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <WarningCircleIcon size={15} weight="fill" color={theme.palette.warning.dark} style={{ flexShrink: 0 }} />
                <DocRef name={state.name} docKey={state.key} />
              </Box>
              <Chip
                label={t('assessment.workflow.domain.wrongFormat')}
                size="small"
                sx={{
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  flexShrink: 0,
                }}
              />
            </Box>
          );

        // Neither standard nor accepted — nothing can validate the file, so
        // this reads as an error and keeps the "won't be submitted" chip.
        case 'unsupported_format':
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'error.lighter',
                borderTop: `1px solid ${theme.palette.error.light}`,
                borderRadius: '0 0 8px 8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <WarningCircleIcon size={15} weight="fill" color={theme.palette.error.dark} style={{ flexShrink: 0 }} />
                <Typography variant="textXS_regular" sx={{ color: 'error.dark', flexShrink: 0 }}>
                  {t('assessment.workflow.domain.unsupportedFormat')}
                </Typography>
              </Box>
              <Chip
                label={<Trans i18nKey="assessments.summary.classification.wontBeSubmitted" />}
                size="small"
                sx={{
                  backgroundColor: 'error.light',
                  color: 'error.dark',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  flexShrink: 0,
                }}
              />
            </Box>
          );

      }
    };

    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: isInvalid
            ? `1px solid ${theme.palette.warning.light}`
            : `1px solid ${theme.palette.grey[400]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Top row: filename + actions */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            py: 1.25,
            px: 2,
          }}
        >
          {/* Left: filename + meta */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, flex: 1, minWidth: 0, alignItems: 'center' }}>
            <Typography
              variant="textSM_semiBold"
              sx={{
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 1,
              }}
              title={document.fileName}
            >
              {document.fileName}
            </Typography>

            {/* Re-assessment provenance chip (FROM V{N} / NEW). Renders nothing
                when there is no parent version or no changeType annotation. */}
            <AssessmentDocumentChangeBadge
              changeType={document.changeType}
              parentVersion={parentVersion}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <Badge label={domainName} variant="DomainName" sx={{ backgroundColor: 'blue.200', borderColor: 'blue.300' }} />
              <Typography variant="textSM_regular" sx={{ color: 'blue.700', whiteSpace: 'nowrap' }}>
                <Trans
                  i18nKey="assessments.summary.uploadedOn"
                  values={{ date: formatUploadDate(document.uploadedAt) }}
                />{' '}
                · {formatFileSize(document.fileSize)}
              </Typography>
            </Box>
          </Box>

          {/* Right: actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {onView && (
              <IconButton
                size="small"
                onClick={handleView}
                aria-label={t('app.assessments.ariaLabel.viewDocument')}
                sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <EyeIcon size={20} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleDownload}
              aria-label={t('app.assessments.ariaLabel.downloadDocument')}
              sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <DownloadSimpleIcon size={20} />
            </IconButton>
          </Box>
        </Box>

        {/* Classification strip */}
        {renderClassificationRow()}
      </Paper>
    );
  },
);
